import React, { createRef, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useRecoilState, useSetRecoilState } from "recoil";

import {
  createGoogleUser,
  createUser,
  temporaryUseAccessToken,
} from "lib/store/user";
import {
  customToastState,
  errorToastState,
  modalState,
} from "lib/store/common";

import {
  Button,
  ButtonLike,
  Checkbox,
  Input,
  TextDivider,
} from "@atsent/components";

import { GoogleSignIn } from "components/atom/GoogleSignIn";
import { HeaderNFooter } from "components/template/HeaderNFooter";
import { Spinner } from "components/atom/Spinner";

import "./SignUpPage.scss";

type SignUpProps = {
  email: string;
  username: string;
  password1: string;
  password2: string;
  agreement: boolean;
  marketing: boolean;
};

const imageF = process.env.PUBLIC_URL + "/static-image";

const Step0 = ({
  setStep,
  inputState,
}: {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  inputState: [SignUpProps, React.Dispatch<React.SetStateAction<SignUpProps>>];
}) => {
  const setToast = useSetRecoilState(customToastState);
  const [input, setInput] = inputState;

  return (
    <>
      <span className="title">무료로 시작하세요.</span>
      <span className="desc">Beta 버전은 모두 무료입니다.</span>
      <Input
        label={"유저 이름"}
        placeholder={"유저 이름 입력"}
        handleOnChange={(e: any) =>
          setInput((input) => ({ ...input, username: e.target.value }))
        }
        removable
      />
      <Input
        label={"이메일"}
        placeholder={"이메일 입력"}
        handleOnChange={(e: any) =>
          setInput((input) => ({ ...input, email: e.target.value }))
        }
        removable
      />
      <Input
        label={"비밀번호"}
        type={"password"}
        placeholder={"문자, 숫자, 기호 조합 8자 이상"}
        handleOnChange={(e: any) =>
          setInput((input) => ({ ...input, password1: e.target.value }))
        }
        removable
        passwordVisible
      />
      <Input
        label={"비밀번호 확인"}
        type={"passwordConfirm"}
        placeholder={"비밀번호 확인"}
        handleOnChange={(e: any) =>
          setInput((input) => ({ ...input, password2: e.target.value }))
        }
        passwordVisible
        removable
      />
      {/*<Input label={'휴대폰 번호'} placeholder={'-없이 입력'} removable />*/}
      <div className={"side-by-side"}>
        <Button
          click={() => {
            if (!input.username) {
              setToast({
                type: "error",
                text: "username을 입력 해 주세요.",
              });
            } else if (!input.email) {
              setToast({
                text: "이메일을 입력 해 주세요..",
                type: "error",
              });
            } else if (!input.password1 || !input.password2) {
              setToast({
                text: "비밀번호를 입력 해 주세요.",
                type: "error",
              });
            } else if (input.password1 !== input.password2) {
              setToast({
                text: "비밀번호를 확인 해 주세요.",
                type: "error",
              });
            } else if (input.password1.length < 8) {
              setToast({
                text: "비밀번호가 너무 짧습니다.",
                type: "error",
              });
            } else if (
              !input.password1.match(/^(?=.*\d)(?=.*[a-zA-Z]).{8,}$/g)
            ) {
              setToast({
                text: "비밀번호는 최소한 8자 이상의 숫자와 알파벳으로 이루어져야합니다.",
                type: "error",
              });
            } else {
              setStep(1);
            }
          }}
        >
          다음 단계로
        </Button>
        <span>
          이미 계정이 있으신가요? <Link to={"/sign-in"}>로그인</Link>
        </span>
      </div>
      <TextDivider text="Or" />
      <GoogleSignIn />
    </>
  );
};

const Step1 = ({
  handleSignUp,
  inputState,
  isLoading,
}: {
  handleSignUp: any;
  inputState: [SignUpProps, React.Dispatch<React.SetStateAction<SignUpProps>>];
  isLoading: boolean;
}) => {
  const [input, setInput] = inputState;

  const setToast = useSetRecoilState(customToastState);
  const setModal = useSetRecoilState(modalState);

  const ppRef = createRef<HTMLInputElement>();
  const termRef = createRef<HTMLInputElement>();
  const marketRef = createRef<HTMLInputElement>();

  const handleCheckAgree = () => {
    if (!ppRef.current || !termRef.current) return;
    if (ppRef.current.checked && termRef.current.checked) {
      setInput((_: SignUpProps) => ({
        ..._,
        agreement: true,
      }));
    }
  };
  return (
    <>
      <span className="title">약관 동의</span>
      <span className="desc">Beta 버전은 모두 무료입니다.</span>
      <div className="checkboxes">
        <Checkbox
          cx={"outline"}
          label={"모두 동의합니다."}
          onChange={(e: any) => {
            if (!termRef.current) return;
            termRef.current.checked = e.target.checked;
            if (!ppRef.current) return;
            ppRef.current.checked = e.target.checked;
            if (!marketRef.current) return;
            marketRef.current.checked = e.target.checked;
            setInput((_: SignUpProps) => ({
              ..._,
              agreement: e.target.checked,
              marketing: e.target.checked,
            }));
          }}
        />

        <Checkbox
          forwardRef={termRef}
          onChange={() => handleCheckAgree()}
          label={
            <>
              <span
                className={"href"}
                onClick={() => setModal((_) => ({ ..._, term: true }))}
              >
                이용약관
              </span>
              에 동의합니다. (필수)
            </>
          }
        />
        <Checkbox
          forwardRef={ppRef}
          onChange={() => handleCheckAgree()}
          label={
            <>
              <span
                className={"href"}
                onClick={() => setModal((_) => ({ ..._, pp: true }))}
              >
                개인정보처리방침
              </span>
              에 동의합니다. (필수)
            </>
          }
        />
        <Checkbox
          forwardRef={marketRef}
          onChange={(e) =>
            setInput((_: SignUpProps) => ({
              ..._,
              marketing: e.target.checked,
            }))
          }
          label={
            <>
              <span
                className={"href"}
                onClick={() => setModal((_) => ({ ..._, market: true }))}
              >
                마케팅 메시지 수신
              </span>
              에 동의합니다. (선택)
            </>
          }
        />
      </div>
      {isLoading ? (
        <ButtonLike>
          <Spinner bgColor={"black"} />
        </ButtonLike>
      ) : (
        <Button
          click={() => {
            if (input.agreement) {
              handleSignUp();
            } else {
              setToast({ type: "error", text: "필수항목에 동의 해 주세요." });
            }
          }}
          cx={"center"}
        >
          가입하기
        </Button>
      )}
    </>
  );
};

export const SignUpPage = () => {
  const navigate = useNavigate();
  const setToast = useSetRecoilState(customToastState);
  const setErrorToast = useSetRecoilState(errorToastState);
  const [tempToken, setTempToken] = useRecoilState(temporaryUseAccessToken);
  const [step, setStep] = useState(0);
  const [input, setInput] = useState<SignUpProps>({
    email: "",
    password1: "",
    password2: "",
    username: "",
    agreement: false,
    marketing: false,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!!tempToken.user) {
      setInput((_) => {
        if (!tempToken.user) return _;
        return {
          ..._,
          email: tempToken.user.email,
          username: tempToken.user.username,
        };
      });
    }
    // eslint-disable-next-line
  }, []);

  const handleSignup = async () => {
    await setLoading(true);
    try {
      await createUser(input);
      await setStep(0);
      await setToast({
        type: "success",
        text: "입력해주신 이메일로 확인 이메일이 전송되었습니다.",
      });
      await navigate("/sign-in");
    } catch (e) {
      setErrorToast(e);
    }

    await setLoading(false);
  };

  const handleGoogleSignUp = async () => {
    await setLoading(true);
    try {
      await createGoogleUser(input);
      await setStep(0);
      await setTempToken({});
      await setToast({
        type: "success",
        text: "회원가입이 완료되었습니다..",
      });
      await navigate("/");
    } catch (e) {
      setErrorToast(e);
    }

    await setLoading(false);
  };
  return (
    <HeaderNFooter cx={"SignUpPage"}>
      <section>
        <div className="left">
          {step === 0 ? (
            <picture>
              <source
                srcSet={`${imageF}/signup_image.webp 1x,${imageF}/signup_image@2x.webp 2x,${imageF}/signup_image@3x.webp 3x`}
                type="image/webp"
              />
              <source
                srcSet={`${imageF}/signup_image.png 1x,${imageF}/signup_image@2x.png 2x,${imageF}/signup_image@3x.png 3x`}
                type="image/png"
              />
              <img src={imageF + "/signup_image.webp"} alt="ggamangi" />
            </picture>
          ) : (
            <picture className={"sizing"}>
              <source
                srcSet={`${imageF}/signup2_image.webp 1x,${imageF}/signup2_image@2x.webp 2x,${imageF}/signup2_image@3x.webp 3x`}
                type="image/webp"
              />
              <source
                srcSet={`${imageF}/signup2_image.png 1x,${imageF}/signup2_image@2x.png 2x,${imageF}/signup2_image@3x.png 3x`}
                type="image/png"
              />
              <img src={imageF + "/signup2_image.webp"} alt="ggamangi" />
            </picture>
          )}
        </div>
        <div className="right">
          <div className="wrapper">
            {!tempToken.user ? (
              step === 0 ? (
                <Step0 setStep={setStep} inputState={[input, setInput]} />
              ) : (
                <Step1
                  handleSignUp={handleSignup}
                  inputState={[input, setInput]}
                  isLoading={loading}
                />
              )
            ) : (
              <Step1
                handleSignUp={handleGoogleSignUp}
                inputState={[input, setInput]}
                isLoading={loading}
              />
            )}
          </div>
        </div>
      </section>
    </HeaderNFooter>
  );
};
